import React from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import ActiveSubscribersTable from "./ActiveSubscribersTable";
import PurchasesTable from "./PurchasesTable";
import UnsubscribersTable from "./UnsubscribersTable";
import "../../../css/admin/admin.scss";
import "../../../css/admin/analytics.scss";
import useAbility from "../../../hooks/useAbility";
import usePeriodFilter from "../../../hooks/usePeriodFilter";
import PageTitle from "../../common/PageTitle";
import InfoPanel from "../../utils/InfoPanel";
import AnalyticsPage from "../AnalyticsPage";
import AnalyticsPeriodPicker from "../charts/AnalyticsPeriodPicker";
import CustomerKpiGrid from "../kpi/CustomerKpiGrid";

import type { JSX } from "react";

import type { AppState } from "../../../reducers";

const infoPanelLinks = {
  en: "https://support.okast.tv/en/support/solutions/articles/103000101760-analytics-sales-subscriptions-and-unsubscriptions-churn-",
  fr: "https://support.okast.tv/fr/support/solutions/articles/103000101762-rapports-d-analyse-suivre-les-transactions-les-abonnements-et-les-d%C3%A9sabonnements",
};

function TransactionsAnalyticsAdmin(): JSX.Element {
  const { t } = useTranslation("editor-analytics");
  const orgaOptions = useSelector(
    (state: AppState) =>
      state.fetchReducer.orga_config.data?.organization_options,
  );

  const breadcrumbs = [
    {
      name: t("editor-menu:analytics"),
    },
  ];
  const { canAccess } = useAbility();
  const { project: canAccessProject } = canAccess(orgaOptions);
  const { period, setPeriod } = usePeriodFilter();

  return (
    <AnalyticsPage
      actions={<AnalyticsPeriodPicker onChange={setPeriod} period={period} />}
      breadcrumbList={breadcrumbs}
      heading={
        <PageTitle>
          {t("editor-menu:transactions_analytics")}
          <InfoPanel links={infoPanelLinks} />
        </PageTitle>
      }
    >
      <CustomerKpiGrid />
      <ActiveSubscribersTable
        offerAbility={canAccessProject.salesWrite}
        period={period}
        userAbility={canAccessProject.usersAnalytics}
      />
      <UnsubscribersTable
        offerAbility={canAccessProject.salesWrite}
        period={period}
        userAbility={canAccessProject.usersAnalytics}
      />
      <PurchasesTable
        offerAbility={canAccessProject.salesWrite}
        period={period}
        userAbility={canAccessProject.usersAnalytics}
      />
    </AnalyticsPage>
  );
}

export default TransactionsAnalyticsAdmin;
